import React, { useMemo } from "react";
import { VirtualItem } from "@tanstack/react-virtual";
import styles from "./task-list-table.module.css";
import { Task } from "../../types/public-types";
import { getParentWbs } from "../gantt/gantt";

const localeDateStringCache = {};
const toLocaleDateStringFactory =
  (locale: string) =>
  (date: Date, dateTimeOptions: Intl.DateTimeFormatOptions): string => {
    if (!date || date.getTime() === 0) return ""; // Handle invalid dates
    const key = date.toString();
    let lds = localeDateStringCache[key];
    if (!lds) {
      lds = date.toLocaleDateString(locale, dateTimeOptions);
      localeDateStringCache[key] = lds;
    }
    return lds;
  };
const dateTimeOptions: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "numeric",
  day: "numeric",
};

export const TaskListTableDefault: React.FC<{
  rowHeight: number;
  rowWidth: string;
  fontFamily: string;
  fontSize: string;
  locale: string;
  tasks: Task[];
  scheduleType: string;
  leafTasks: Task[];
  selectedTaskId: string;
  setSelectedTask: (taskId: string) => void;
  onExpanderClick: (task: Task) => void;
  selectedTasks?: string[];
  onTaskSelect?: (taskId: string, selected: boolean) => void;
  taskLabelRenderer?: (Task: Task) => React.ReactNode;
  virtualItems?: VirtualItem[];
  columnWidths?: Record<string, number>;
}> = ({
  rowHeight,
  rowWidth,
  tasks,
  scheduleType,
  leafTasks,
  fontFamily,
  fontSize,
  locale,
  onExpanderClick,
  selectedTasks = [],
  onTaskSelect,
  taskLabelRenderer = t => " " + t.name,
  virtualItems,
  columnWidths,
}) => {
  // Per-column widths shared with the header (so resize stays in sync); falls
  // back to the default factor × rowWidth when no width has been set.
  const widthOf = (colId: string, factor: number) =>
    columnWidths?.[colId] ?? parseInt(rowWidth) * factor;
  const toLocaleDateString = useMemo(
    () => toLocaleDateStringFactory(locale),
    [locale]
  );
  const leafTaskIds = useMemo(
    () => new Set(leafTasks.map(t => t.id)),
    [leafTasks]
  );

  // Checks if the task has a selected ancestor by traversing the WBS hierarchy
  const hasSelectedAncestor = (taskId: string, selectedSet: Set<string>, allTasks: Task[]): boolean => {
    const task = allTasks.find(t => t.id === taskId);
    if (!task) return false;
    const parentWbs = getParentWbs(task.id);
    if (!parentWbs) return false;
    if (selectedSet.has(parentWbs)) return true;
    return hasSelectedAncestor(parentWbs, selectedSet, allTasks);
  };

  const itemsToRender = virtualItems || tasks.map((_, index) => ({ index, start: index * rowHeight, size: rowHeight, key: index, end: (index + 1) * rowHeight, lane: 0, measureElement: () => {} }));

  return (
    <div
      className={styles.taskListWrapper}
      style={{
        fontFamily: fontFamily,
        fontSize: fontSize,
      }}
    >
      {itemsToRender.map(vi => {
        const t = tasks[vi.index];
        let expanderSymbol = "";
        if (!(leafTaskIds.has(t.id)  || t.type=== "milestone")) {
          if (t.hideChildren === false) {
            expanderSymbol = "▼";
          } else if (t.hideChildren === true) {
            expanderSymbol = "►";
          }
        }

        const isSelected = selectedTasks.includes(t.id);
        const isAncestorSelected = hasSelectedAncestor(t.id, new Set(selectedTasks), tasks);

        return (
          <div
            key={`${vi.key}`}
            data-index={vi.index}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: `${vi.size}px`,
              transform: `translateY(${vi.start}px)`,
            }}
          >
            <div
              className={
                t.type === "milestone"
                  ? styles.taskListMilestoneRow
                  : scheduleType === "lookAhead"
                  ? styles.taskListLookAheadRow
                  : styles.taskListTableRow
              }
              style={{ height: rowHeight }}
              key={`${t.id}row`}
            >
              {onTaskSelect && (
                <div
                  className={styles.taskListCell}
                  style={{
                    minWidth: widthOf("select", 0.3),
                    maxWidth: widthOf("select", 0.3),
                  }}
                >
                  <div className={styles.taskListText} style={{ 
                    display: "flex", 
                    justifyContent: "center", 
                    alignItems: "center",
                    height: "100%",
                    paddingLeft: "0",
                    paddingRight: "0"
                  }}>
                    <input 
                      type="checkbox" 
                      checked={isSelected}
                      disabled={isAncestorSelected}
                      onChange={(e) => onTaskSelect(t.id, e.target.checked)}
                    />
                  </div>
                </div>
              )}
              <div
                className={styles.taskListCell}
                style={{
                  minWidth: widthOf("id", 0.8),
                  maxWidth: widthOf("id", 0.8),
                }}
                title={t.id}
              >
                <div
                  className={styles.taskListNameWrapper}
                  style={{ paddingLeft: `${t.depth * 4}px` }}
                >
                  {!(leafTaskIds.has(t.id) || t.type=== "milestone") ? (
                    <div
                    className={styles.taskListExpander}
                    onClick={() => onExpanderClick(t)}
                    >
                      
                      {expanderSymbol}
                    </div>
                  ) : (
                    <div className={styles.taskListExpanderPlaceholder}></div>
                  )}
                  <div className={styles.taskListText}>
                    {t.id}{" "}
                    {t.actualEnd.getTime() > 0 &&
                    t.actualEnd.getTime() < Date.now() ? (
                      <span
                        title="Task Complete"
                        style={{ color: "limegreen", fontSize: "16px" }}
                      >
                        ✔
                      </span>
                    ) : (
                      ""
                    )}
                  </div>
                </div>
              </div>
              <div
                className={styles.taskListCell}
                style={{
                  minWidth: widthOf("wbs", 0.8),
                  maxWidth: widthOf("wbs", 0.8),
                }}
                title={t.optionalId?t.optionalId:""}
              >
               {t.optionalId}
              </div>
              <div
                className={styles.taskListCell}
                style={{
                  minWidth: widthOf("name", 1.8),
                  maxWidth: widthOf("name", 1.8),
                }}
                title={t.name}
              >
                <div className={styles.taskListText}>{taskLabelRenderer(t)}</div>
              </div>
              <div
                className={styles.taskListCell}
                style={{
                  minWidth: widthOf("plannedStart", 0.6),
                  maxWidth: widthOf("plannedStart", 0.6),
                }}
              >
                <div className={styles.taskListText}>
                  &nbsp;{toLocaleDateString(t.start, dateTimeOptions)}
                </div>
              </div>
              <div
                className={styles.taskListCell}
                style={{
                  minWidth: widthOf("plannedEnd", 0.6),
                  maxWidth: widthOf("plannedEnd", 0.6),
                }}
              >
                <div className={styles.taskListText}>
                  &nbsp;{toLocaleDateString(t.end, dateTimeOptions)}
                </div>
              </div>
              {scheduleType === "lookAhead" && (
                <div
                  className={styles.taskListCell}
                  style={{
                    minWidth: widthOf("actualStart", 0.6),
                    maxWidth: widthOf("actualStart", 0.6),
                  }}
                >
                  <div className={styles.taskListText}>
                    &nbsp;{toLocaleDateString(t.actualStart, dateTimeOptions)}
                  </div>
                </div>
              )}
              {scheduleType === "lookAhead" && (
                <div
                  className={styles.taskListCell}
                  style={{
                    minWidth: widthOf("actualEnd", 0.6),
                    maxWidth: widthOf("actualEnd", 0.6),
                  }}
                >
                  <div className={styles.taskListText}>
                    &nbsp;{toLocaleDateString(t.actualEnd, dateTimeOptions)}
                  </div>
                </div>
              )}
              {scheduleType === "main" && (() => {
                const percentComplete = t.percentComplete != null ? t.percentComplete : (t.progress ?? 0);
                const plannedDuration = t.plannedDuration != null ? t.plannedDuration : null;
                const remainingDuration = plannedDuration != null
                  ? Math.max(Math.round(plannedDuration - (plannedDuration * (percentComplete / 100))), 0)
                  : null;
                const actualDuration = (() => {
                  if (!t.actualStart || t.actualStart.getTime() === 0) return null;
                  const endRef = (t.actualEnd && t.actualEnd.getTime() > 0) ? t.actualEnd : new Date();
                  return Math.ceil((endRef.getTime() - t.actualStart.getTime()) / (1000 * 60 * 60 * 24));
                })();
                return (
                  <React.Fragment>
                    <div
                      className={styles.taskListCell}
                      style={{
                        minWidth: widthOf("percentComplete", 0.6),
                        maxWidth: widthOf("percentComplete", 0.6),
                      }}
                    >
                      <div className={styles.taskListText}>
                        &nbsp;{percentComplete != null ? `${percentComplete}%` : ""}
                      </div>
                    </div>
                    <div
                      className={styles.taskListCell}
                      style={{
                        minWidth: widthOf("plannedDuration", 0.6),
                        maxWidth: widthOf("plannedDuration", 0.6),
                      }}
                    >
                      <div className={styles.taskListText}>
                        &nbsp;{plannedDuration != null ? plannedDuration : ""}
                      </div>
                    </div>
                    <div
                      className={styles.taskListCell}
                      style={{
                        minWidth: widthOf("remainingDuration", 0.7),
                        maxWidth: widthOf("remainingDuration", 0.7),
                      }}
                    >
                      <div className={styles.taskListText}>
                        &nbsp;{remainingDuration != null ? remainingDuration : ""}
                      </div>
                    </div>
                    <div
                      className={styles.taskListCell}
                      style={{
                        minWidth: widthOf("actualDuration", 0.6),
                        maxWidth: widthOf("actualDuration", 0.6),
                      }}
                    >
                      <div className={styles.taskListText}>
                        &nbsp;{actualDuration != null ? actualDuration : ""}
                      </div>
                    </div>
                    <div
                      className={styles.taskListCell}
                      style={{
                        minWidth: widthOf("durationType", 0.8),
                        maxWidth: widthOf("durationType", 0.8),
                      }}
                    >
                      <div className={styles.taskListText}>
                        &nbsp;{t.durationType ?? "Activity Calendar"}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })()}
            </div>
          </div>
        );
      })}
    </div>
  );
};